import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {Limit} from "crqlar-react-common/dist/models/common/limit";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {FOLIO_DIALOG_LIST_KEY} from "./folioQueryKeys";
import {sortFoliosByDate} from "./folioList.util";

/**
 * Whose folios: a reservation's - with its package's for a package's member: the package's one folio
 * has no reservation, only its group -, or for a folio without a reservation its group's.
 */
export interface FolioListScope {
    reservationId?: string
    reservationGroupId?: string
}

export const folioListKey = ({reservationId, reservationGroupId}: FolioListScope) => (
    [FOLIO_DIALOG_LIST_KEY, reservationId, reservationGroupId]
);

const ALL_OLDEST_FIRST: Limit = {offset: 0, limit: 100, sort: [{field: 'insertDate', direction: 'ASC'}]};

/** All the folios of the scope, oldest first - a reservation has only a few. */
export const fetchFolioList = async ({reservationId, reservationGroupId}: FolioListScope): Promise<Folio[]> => {
    // The list narrows by every filter it gets: the reservation's folios and the group's are two reads
    const pages = await Promise.all([
        reservationId
            ? FolioService.getAll({reservationIds: [reservationId], reservationGroupIds: undefined, limit: ALL_OLDEST_FIRST})
            : undefined,
        reservationGroupId
            ? FolioService.getAll({reservationIds: undefined, reservationGroupIds: [reservationGroupId], limit: ALL_OLDEST_FIRST})
            : undefined,
    ]);
    const folios = pages.flatMap(page => page?.content ?? []);
    // A folio of the reservation and its group is listed once
    return sortFoliosByDate(folios.filter((folio, index) => folios.findIndex(other => other.id === folio.id) === index));
};

/** The folios of the scope, oldest first; shared by the list view and the header's folio name. */
export const useFolioList = ({reservationId, reservationGroupId}: FolioListScope, enabled: boolean) => useQuery(
    folioListKey({reservationId, reservationGroupId}),
    () => fetchFolioList({reservationId, reservationGroupId}),
    {enabled: enabled && (!!reservationId || !!reservationGroupId), refetchOnWindowFocus: false}
);

/** A folio's name, or "Folio 2" by its place in the list for one without. */
export const useFolioName = () => {
    const {t} = useTranslation();
    return useCallback((folio: Folio | undefined, number?: number) => (
        folio?.name?.trim() || (number ? t('Folio {{number}}', {number}) : t('Folio'))
    ), [t]);
};
